import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";
import { env } from "@/redux/http";

/**
 * Environments in which Sentry should report. Only `prod` is enabled — `dev`
 * and `demo` are intentionally excluded so non-production traffic never
 * pollutes the dashboard. `env` is controlled in `src/redux/http.ts`.
 */
const ENABLED_ENVIRONMENTS: readonly string[] = ["prod"];

/**
 * HTTP statuses surfaced by the API layer (`src/lib/baseQuery.ts`) that are
 * expected during normal use (an unauthenticated/forbidden response triggers a
 * logout, it is not a bug) and should never be reported as errors.
 */
const IGNORED_HTTP_STATUSES: readonly number[] = [401, 403];

/**
 * Error messages we never want in Sentry: browser/runtime chatter and
 * cancelled in-flight requests. RTK Query aborts requests on unmount/refetch,
 * and the kitchen tablets run on flaky Wi-Fi, so these are expected noise.
 */
export const ignoreErrors: (string | RegExp)[] = [
  // Cancelled / aborted fetches
  "AbortError",
  "The user aborted a request",
  "The operation was aborted",
  "signal is aborted without reason",
  // Network blips (offline tablets, flaky Wi-Fi)
  "Failed to fetch",
  "NetworkError when attempting to fetch resource",
  "Load failed",
  "Network request failed",
  // Benign ResizeObserver chatter
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  // Non-actionable promise rejections
  "Non-Error promise rejection captured",
];

export interface SentryRuntimeConfig {
  env: string;
  dsn: string | undefined;
  release: string | undefined;
}

/** Resolve the runtime config from the app env and Vite-injected build vars. */
function resolveConfig(): SentryRuntimeConfig {
  return {
    env,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE,
  };
}

/** Sentry only runs when a DSN is configured and the env opts in. */
export function isSentryEnabled(
  currentEnv: string = env,
  dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN
): boolean {
  return Boolean(dsn) && ENABLED_ENVIRONMENTS.includes(currentEnv);
}

function getStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status;
    if (typeof status === "number") return status;
  }
  return undefined;
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = (error as { name?: unknown }).name;
  return name === "AbortError" || name === "CanceledError";
}

/**
 * Last-line noise filter applied to every event before it leaves the browser.
 * Drops expected auth failures from the API layer and cancelled requests that
 * `ignoreErrors` (message-based) can miss.
 */
export function beforeSend(
  event: ErrorEvent,
  hint?: EventHint
): ErrorEvent | null {
  const original = hint?.originalException;

  const status = getStatusFromError(original);
  if (status !== undefined && IGNORED_HTTP_STATUSES.includes(status)) {
    return null;
  }

  if (isAbortError(original)) return null;

  return event;
}

/**
 * Initialise Sentry. No-op (returns false) when disabled so callers don't need
 * to guard. Defaults read from the app env and build, but accept an explicit
 * config for testing.
 */
export function initSentry(config: SentryRuntimeConfig = resolveConfig()): boolean {
  if (!isSentryEnabled(config.env, config.dsn)) return false;

  Sentry.init({
    dsn: config.dsn,
    environment: config.env,
    release: config.release,
    integrations: [Sentry.browserTracingIntegration()],
    // Performance: sample a fraction of transactions to keep volume sane.
    tracesSampleRate: 0.1,
    ignoreErrors,
    beforeSend,
  });

  return true;
}
